import { Euler, InstancedBufferAttribute, InstancedMesh, Object3D, RawShaderMaterial } from "three";
import flightsVertexShader from "./shaders/vertex.glsl";
import flightsFragmentShader from "./shaders/fragment.glsl";
import { sphericalToCartesianThreeJs } from "../common/Coordinates";
import { EARTH_RADIUS } from "@common/utils/Constants";
import { getUnix } from "@common/utils/Time";
const MAX_R = 1.02;
const MIN_R = 1.0001;
const MIN_EJECT_R = 1.005;
const MIN_SCALE = 0.0022;
const MAX_SCALE = 12;
const EJECT_SCALE = 4;
export default class FlightInstances {
    scale = 1;
    t0;
    r;
    flights;
    mesh;
    constructor(model, time, flights) {
        this.t0 = time;
        this.r = MAX_R;
        this.flights = flights;
        const material = new RawShaderMaterial({
            vertexShader: flightsVertexShader,
            fragmentShader: flightsFragmentShader
        });
        const geometry = model.clone();
        const roles = flights.map((({ role }) => role));
        geometry.setAttribute("aRole", new InstancedBufferAttribute(new Float32Array(roles), 1, false, 1));
        const count = flights.length;
        this.mesh = new InstancedMesh(geometry, material, count);
        const empty = new Object3D();
        for (let i = 0; i < count; i++) {
            const flight = flights[i];
            flight.position.r = this.r;
            const threeCoordinates = sphericalToCartesianThreeJs(flight.position);
            empty.setRotationFromEuler(new Euler(flight.position.theta, flight.position.phi + (Math.PI / 2), 0, "YXZ"));
            empty.position.set(threeCoordinates.x, threeCoordinates.y, threeCoordinates.z);
            empty.rotateY(-flight.speed.psi);
            empty.updateMatrix();
            this.mesh.setMatrixAt(i, empty.matrix);
        }
        this.mesh.instanceMatrix.needsUpdate = true;
    }
    update(λCamera, eject) {
        const desiredScale = eject ? EJECT_SCALE : MIN_SCALE * (1 - λCamera) + MAX_SCALE * λCamera;
        const newScale = desiredScale / this.scale;
        this.scale *= newScale;
        this.mesh.geometry.scale(newScale, newScale, newScale);
        const empty = new Object3D();
        const count = this.flights.length;
        for (let i = 0; i < count; i++) {
            const flight = this.flights[i];
            const sphericalCoordinates = {
                r: flight.position.r,
                theta: flight.position.theta,
                phi: flight.position.phi + Math.PI / 2,
            };
            sphericalCoordinates.r = eject ? MIN_EJECT_R + (0.00125 * (1 + Math.sin(10 * getUnix()))) : MIN_R * (1 - λCamera) + MAX_R * λCamera;
            const angularSpeed = flight.speed.value / EARTH_RADIUS;
            const thetaTilde = angularSpeed * (getUnix() - this.t0);
            const cosThetaTilde = Math.cos(thetaTilde);
            const sinThetaTilde = Math.sin(thetaTilde);
            const cosTheta = Math.cos(sphericalCoordinates.theta);
            const sinTheta = Math.sin(sphericalCoordinates.theta);
            const cosPhi = Math.cos(sphericalCoordinates.phi);
            const sinPhi = Math.sin(sphericalCoordinates.phi);
            const cosPsi = Math.cos(flight.speed.psi);
            const sinPsi = Math.sin(flight.speed.psi);
            const x = cosThetaTilde * sinTheta * cosPhi - sinThetaTilde * cosPsi * cosTheta * cosPhi - sinThetaTilde * sinPsi * sinPhi;
            const y = cosThetaTilde * sinTheta * sinPhi - sinThetaTilde * cosPsi * cosTheta * sinPhi + sinThetaTilde * sinPsi * cosPhi;
            const z = cosThetaTilde * cosTheta + sinThetaTilde * cosPsi * sinTheta;
            empty.position.set(sphericalCoordinates.r * y, sphericalCoordinates.r * z, sphericalCoordinates.r * x);
            empty.setRotationFromEuler(new Euler(sphericalCoordinates.theta, sphericalCoordinates.phi, 0, "YXZ"));
            empty.rotateY(-flight.speed.psi);
            empty.updateMatrix();
            this.mesh.setMatrixAt(i, empty.matrix);
        }
        this.mesh.instanceMatrix.needsUpdate = true;
    }
    dispose() {
        this.mesh.geometry.dispose();
        this.mesh.material.dispose();
    }
    getObject3d() {
        return this.mesh;
    }
}
